import fetch from './index'

export type IdpWebKey = 'FG' | 'ST' | 'BF'

export interface IdpOverviewDTO {
  webKey: IdpWebKey
  pv: number
  uv: number
  clicks: number
  dau: number
  wau: number
  mau: number
  trend?: Array<{ day: string; pv: number; uv: number; clicks: number }>
  identityDistribution?: Array<{ name: string; value: number }>
  deviceDistribution?: Array<{ name: string; value: number }>
  eventTypeDistribution?: Array<{ name: string; value: number }>
}

export interface IdpRealtimeEventDTO {
  eventId: string
  eventTime: number
  eventType: string
  webKey: IdpWebKey
  deviceId: string
  sessionId: string
  customerId?: string
  deviceType?: string
  pageId?: number
  pageUrl?: string
  elementId?: number
  elementText?: string
  referrer?: string
  scrollDepth?: number
  engagementMs?: number
  props?: Record<string, any>
}

export interface IdpSessionDetailDTO {
  sessionId: string
  deviceId: string
  customerId?: string
  deviceType?: string
  entryUrl?: string
  exitUrl?: string
  startedAt?: number
  endedAt?: number
  durationMs?: number
  eventCount?: number
  events?: IdpRealtimeEventDTO[]
}

export interface IdpSessionSummaryDTO {
  sessionId: string
  deviceId: string
  customerId?: string
  deviceType?: string
  entryUrl?: string
  exitUrl?: string
  startedAt?: number
  endedAt?: number
  durationMs?: number
  eventCount?: number
}

export interface IdpSessionListDTO {
  webKey: IdpWebKey
  cursor?: string
  nextCursor?: string
  pageSize?: number
  sessions?: IdpSessionSummaryDTO[]
  detail?: IdpSessionDetailDTO
}

export interface IdpFunnelDTO {
  webKey: IdpWebKey
  steps?: Array<{
    stepIndex: number
    stepKey: string
    label: string
    users: number
    conversionRate: number
    dropOffRate: number
  }>
}

export interface IdpHeatmapDTO {
  webKey: IdpWebKey
  pageId: number
  pageUrl?: string
  deviceBucket?: string
  clicks?: Array<{
    elementId?: number
    elementSelector?: string
    elementText?: string
    xPct?: number
    yPct?: number
    clicks: number
    uniqueUsers: number
  }>
  scrollBuckets?: Array<{ bucket: number; users: number }>
}

export interface IdpDataHealthDTO {
  webKey: IdpWebKey
  healthy: boolean
  kpis?: Array<{ name: string; status: string; value: number; target: number }>
  rollupLags?: Array<{ tableName: string; watermarkTime?: number; lagSeconds?: number; status: string }>
  fieldFillRates?: Array<{ fieldName: string; fillRate: number; status: string }>
}

interface IdpBaseParams {
  webKey: IdpWebKey
  from?: string
  to?: string
}

function cleanParams(params: Record<string, any>) {
  return Object.keys(params).reduce((acc, key) => {
    const value = params[key]
    if (value !== undefined && value !== null && value !== '') {
      acc[key] = value
    }
    return acc
  }, {} as Record<string, any>)
}

function serializeRepeatedParams(params: Record<string, any>) {
  const search = new URLSearchParams()
  Object.keys(params || {}).forEach(key => {
    const value = params[key]
    if (Array.isArray(value)) {
      value.forEach(item => search.append(key, String(item)))
      return
    }
    search.append(key, String(value))
  })
  return search.toString()
}

export function getIdpOverview(params: IdpBaseParams): Promise<IdpOverviewDTO> {
  return fetch.get('/idp/analytics/overview', {
    params: cleanParams(params),
    noLoading: true,
  })
}

export function getIdpSessions(params: IdpBaseParams & {
  filters?: Record<string, any>
  cursor?: string
  sessionId?: string
}): Promise<IdpSessionListDTO> {
  return fetch.get('/idp/analytics/sessions', {
    params: cleanParams({
      ...params,
      filters: params.filters ? JSON.stringify(params.filters) : undefined,
    }),
    noLoading: true,
  })
}

export function getIdpFunnel(params: IdpBaseParams & { steps?: string[] }): Promise<IdpFunnelDTO> {
  return fetch.get('/idp/analytics/funnel', {
    params: cleanParams(params),
    paramsSerializer: serializeRepeatedParams,
    noLoading: true,
  })
}

export function getIdpHeatmap(params: {
  webKey: IdpWebKey
  pageId?: number
  deviceBucket?: string
  from?: string
  to?: string
  url?: string
}): Promise<IdpHeatmapDTO> {
  return fetch.get('/idp/analytics/heatmap', {
    params: cleanParams(params),
    noLoading: true,
  })
}

export interface IdpAggregateRow {
  elementId?: number
  selector?: string
  elementText?: string
  semanticRole?: string
  eventType?: string
  pv: number
  uv: number
}

export interface IdpAggregateDTO {
  webKey: IdpWebKey
  rows?: IdpAggregateRow[]
}

export interface IdpPathFlowNode {
  pageId: number
  url?: string
  title?: string
  pv: number
  exits: number
}

export interface IdpSankeyNode {
  id: string
  label: string
  depth: number
  users: number
  pageType?: string
}

export interface IdpPathFlowDTO {
  webKey: IdpWebKey
  nodes?: IdpPathFlowNode[]
  links?: Array<{ sourcePageId: number; targetPageId: number; value: number }>
  sankeyNodes?: IdpSankeyNode[]
  sankeyLinks?: Array<{ source: string; target: string; value: number }>
  sankeyTotalStart?: number
}

export function getIdpAggregate(params: IdpBaseParams & {
  deviceBucket?: string
  destFunction?: string
}): Promise<IdpAggregateDTO> {
  return fetch.get('/idp/analytics/aggregate', {
    params: cleanParams(params),
    noLoading: true,
  })
}

export function getIdpDestFunctions(params: { webKey: IdpWebKey }): Promise<string[]> {
  return fetch.get('/idp/analytics/destfunctions', {
    params: cleanParams(params),
    noLoading: true,
  })
}

export function getIdpPathFlow(params: IdpBaseParams & {
  startPageId?: number
  startPageType?: string
  targetPageId?: number
  maxDepth?: number
}): Promise<IdpPathFlowDTO> {
  return fetch.get('/idp/analytics/pathflow', {
    params: cleanParams(params),
    noLoading: true,
  })
}

export interface IdpCohortWeek {
  cohortDate: string
  cohortSize: number
  d0?: number | null
  d1?: number | null
  d3?: number | null
  d7?: number | null
  d14?: number | null
  d30?: number | null
}

export interface IdpCohortDTO {
  webKey: IdpWebKey
  cohorts?: IdpCohortWeek[]
}

export function getIdpCohort(params: { webKey: IdpWebKey; weeks?: number }): Promise<IdpCohortDTO> {
  return fetch.get('/idp/analytics/cohort', {
    params: cleanParams(params),
    noLoading: true,
  })
}

export function getIdpRealtime(params: {
  webKey: IdpWebKey
  limit?: number
}): Promise<IdpRealtimeEventDTO[]> {
  return fetch.get('/idp/analytics/realtime', {
    params: cleanParams(params),
    noLoading: true,
  })
}

export function getIdpHealth(params: { webKey: IdpWebKey }): Promise<IdpDataHealthDTO> {
  return fetch.get('/idp/health/snapshot', {
    params,
    noLoading: true,
  })
}
